import React, { useEffect, useState } from 'react'
import {
  makeStyles,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Typography,
  ConfirmationDialog,
  Grid,
  Skeleton,
  useToggle,
  Spacer
} from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses, DialogProps } from '@paintscout/ui'
import { useCrmDialogs, useUser, useUserPermissions } from '@paintscout/ui-business'
import * as Sentry from '@sentry/react'
import { useSnackbar } from 'notistack'
import ClearIcon from '@material-ui/icons/Clear'
import { useTaskMutations } from 'shared/hooks'
import { useTaskQuery } from '@paintscout/api'
import type { Task } from '@paintscout/api'
import { isUserAssigned, isUserOwner } from '@paintscout/util/builder'
import TaskForm from 'src/components/forms/TaskForm'
import { areTasksLooselyEqual } from '@paintscout/util'
import classNames from 'classnames'
import { track } from 'shared/util/analytics'

const useStyles = makeStyles<Theme, TaskDialogProps>(
  (theme) => ({
    root: {},
    dialogTitle: {},
    dialogContent: {},
    cancel: {},
    confirm: {},
    closeIcon: {
      cursor: 'pointer',
      position: 'absolute',
      right: theme.spacing(2),
      top: theme.spacing(1.5)
    },
    edit: {
      color: theme.paintscout.palette.brand.Neutral.P70
    },
    error: {
      '& span span': {
        color: theme.palette.text.secondary
      },
      '&:hover': {
        backgroundColor: 'transparent' // Remove gray background on hover
      },
      '@media (hover: hover) and (pointer: fine)': {
        '&:hover span span': {
          color: theme.palette.error.main
        }
      },
      '&:active': {
        backgroundColor: 'transparent' // Remove gray background on active
      },
      '&:active span span': {
        color: theme.palette.error.main
      }
    },
    leftAction: {
      [theme.breakpoints.down('sm')]: {
        marginLeft: `-${theme.spacing(0.25)}px`,
        minWidth: 0,
        justifyContent: 'flex-start'
      }
    }
  }),
  { name: 'TaskDialog' }
)

export interface TaskDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof useStyles>
  taskId?: string
  dealId?: string
  dealQuery?: string
  searchCompanyId?: string
  defaultEstimator?: string
  onConfirm: (task: Task) => any
  onCancel: (event: React.MouseEvent<HTMLElement>) => any
  onBack?: (event: React.MouseEvent<HTMLElement>) => any
  onDelete?: (taskId: string) => any
}

function TaskDialog(props: TaskDialogProps) {
  const classes = useStyles(props)
  const {
    onConfirm,
    onCancel,
    taskId,
    dealId,
    onDelete,
    dealQuery,
    defaultEstimator: defaultEstimatorProp,
    searchCompanyId = null,
    onBack,
    ...baseDialogProps
  } = props
  const { openDialog, dismissAllDialogs, dismissDialog, crmContext } = useCrmDialogs()
  const { subtitle, deal: contextDeal } = crmContext || {}
  const { hide: hideDeal } = contextDeal || {}

  const { enqueueSnackbar } = useSnackbar()
  const { user, isSales } = useUser()
  const isCreating = !taskId
  const { value: isSaving, setValue: setSaving } = useToggle(false)
  const { deleteTasks, saveTask } = useTaskMutations()
  const { permissionToEditAllTasks } = useUserPermissions()
  const [initialTaskValues, setInitialTaskValues] = useState<Partial<Task>>(null)
  const [dirty, setIsDirty] = useState(false)

  const defaultEstimator = defaultEstimatorProp || (isCreating && isSales ? user?.user_id || '' : '')

  const {
    data,
    error,
    loading: fetching
  } = useTaskQuery({
    variables: { id: taskId },
    skip: !taskId,
    fetchPolicy: 'cache-and-network'
  })
  const [task, setTask] = useState({ ...data?.task, ...(isCreating ? { dealId: dealId } : {}) })
  const loading = (fetching && !task && !!taskId) || isSaving
  const deleteEnabled =
    (permissionToEditAllTasks || isUserAssigned(task, user.user_id) || isUserOwner(task, user.user_id)) && task?._id
  const [userOverrideEditMode, setUserOverrideEditMode] = useState(false)
  const editMode = isCreating || userOverrideEditMode || (!!data?.task && !data.task.completed)

  useEffect(() => {
    if (error) {
      Sentry.captureException(error, { tags: { taskId } })
      enqueueSnackbar('Unable to load Task', { variant: 'error' })
      onCancel(null)
    }
  }, [error])

  useEffect(() => {
    if (data) {
      setTask(data.task)
      setUserOverrideEditMode(false)
    }
  }, [data])

  const handleClose = () => {
    if (!dirty) {
      dismissAllDialogs()
    } else {
      openDialog(ConfirmationDialog, {
        message: 'You have unsaved changes. If you exit now, your changes will be lost.',
        onConfirm: () => {
          dismissAllDialogs()
        },
        onCancel: () => {
          dismissDialog()
        }
      })
    }
  }

  function handleDelete(_: React.FormEvent<HTMLElement>) {
    openDialog(ConfirmationDialog, {
      message: <span>This will permanently delete this task.</span>,
      onConfirm: async () => {
        try {
          track('task_deleted', { taskId: task._id })
          await deleteTasks([task._id])
          if (onDelete) {
            onDelete(task._id)
          }
          dismissAllDialogs()
        } catch (error) {
          console.log(error)
          Sentry.captureException(error, { tags: { taskId: task._id } })
          enqueueSnackbar('Unable to delete Task', { variant: 'error' })
        }
      },
      onCancel: dismissDialog
    })
  }

  const taskDataReady = isCreating || !!data?.task
  let leftButton = null
  if (!taskDataReady) {
    leftButton = null
  } else if (!editMode) {
    leftButton = (
      <Button
        className={classNames(classes.edit, classes.leftAction)}
        onClick={() => setUserOverrideEditMode(true)}
        variant={'textNoPadding'}
        disabled={loading}
      >
        Edit Task
      </Button>
    )
  } else if (deleteEnabled) {
    leftButton = (
      <Button
        className={classNames(classes.error, classes.leftAction)}
        onClick={handleDelete}
        variant={'textNoPadding'}
        disabled={loading}
      >
        Delete
      </Button>
    )
  }

  return (
    <Dialog {...baseDialogProps} disableEnforceFocus>
      <DialogTitle
        loading={loading}
        subtitle={subtitle}
        rightContent={
          <div onClick={handleClose} className={classes.closeIcon}>
            <ClearIcon />
          </div>
        }
      >
        <Typography variant="h3">{isCreating ? 'Create New' : 'Edit'} Task</Typography>
      </DialogTitle>
      <DialogContent>
        {task || (!loading && !taskId) ? (
          <TaskForm
            id="task-dialog-form"
            data-testid="task-dialog-form"
            onSubmit={handleSubmit}
            disabled={loading || isSaving || !editMode}
            hideDeal={hideDeal}
            dealQuery={dealQuery}
            searchCompanyId={searchCompanyId}
            defaultEstimator={defaultEstimator}
            task={task}
            isNew={isCreating}
            onDialogBack={onBack}
            onDialogCancel={handleClose}
            onEdit={(taskValues: Task) => {
              if (!initialTaskValues && taskId === taskValues._id) {
                setInitialTaskValues(taskValues)
              } else {
                setIsDirty(!areTasksLooselyEqual(initialTaskValues, taskValues, ['_id', 'created']))
              }
            }}
          />
        ) : (
          <EditTaskDialogShim />
        )}
      </DialogContent>
      <DialogActions alignLeftButtonWithTitle={false} leftButton={leftButton}>
        <Button
          className={classNames(classes.confirm, 'tour-task-dialog-confirm-button')}
          type="submit"
          form="task-dialog-form"
          variant={'contained'}
          disabled={isSaving}
          data-testid="task-dialog-confirm-button"
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  )

  async function handleSubmit(newTask: Task) {
    track(isCreating ? 'task_created' : 'task_updated', {
      taskId: newTask._id,
      hasAssignee: !!newTask.owner
    })
    setSaving(true)
    try {
      const saveResult = await saveTask({ task: newTask })
      console.log({ savedTask: saveResult })
      onConfirm(saveResult.task)
    } catch (error) {
      Sentry.captureException(error)
      enqueueSnackbar('error', { variant: 'error' })
    }
    setSaving(false)
  }
}

export default TaskDialog

function EditTaskDialogShim() {
  return (
    <div>
      <Spacer height={10} />
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Skeleton width="100%" height="40px" />
        </Grid>
      </Grid>
      <Spacer height={30} />
      <Grid container spacing={2}>
        <Grid item xs={5}>
          <Skeleton width="100%" height="40px" />
        </Grid>
        <Grid item xs={2}>
          <Skeleton width="100%" height="40px" />
        </Grid>
        <Grid item xs={5}>
          <Skeleton width="100%" height="40px" />
        </Grid>
      </Grid>
      <Spacer height={30} />

      <Grid item xs={12}>
        <Skeleton width="100%" height="200px" />
      </Grid>
      <Spacer height={30} />
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Skeleton width="100%" height="40px" />
        </Grid>
        <Grid item xs={6}>
          <Skeleton width="100%" height="40px" />
        </Grid>
      </Grid>
      <Spacer height={30} />
    </div>
  )
}
